"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared asset thumbnail frame — the single image treatment for every asset
 * card variant (browse grid, home rails, related/upsell, wishlist, cart,
 * purchases, seller/admin tables, studio panels).
 *
 * Contract (see docs/DESIGN_SYSTEM.md §15/§19):
 * - The full image is always visible, uncropped, over a blurred/saturated
 *   backdrop fill of itself — same technique as `AssetImageGallery`'s main
 *   product visual. No card anywhere crops an asset image to fill the frame.
 * - Fade-in on load via inline opacity (the documented blur-up convention).
 * - No-image fallback: first letter of the title on a muted gradient.
 * - Overlays (badges, gradients, stat pills) render as `children` on top.
 *
 * The wrapper is `relative overflow-hidden`; pass sizing via `aspect` or via
 * explicit width/height classes in `className` with `aspect="none"`.
 */
const ASPECT_CLASS = {
  "4/3": "aspect-[4/3]",
  video: "aspect-video",
  square: "aspect-square",
  none: "",
} as const;

export interface AssetThumbProps {
  src: string | null | undefined;
  alt: string;
  /** Source for the fallback letter; defaults to `alt`. */
  title?: string;
  aspect?: keyof typeof ASPECT_CLASS;
  sizes?: string;
  priority?: boolean;
  /**
   * Render visible immediately — no opacity-0 + JS fade. Use for the single
   * above-the-fold priority image (hero spotlight): the fade-in pattern makes
   * the LCP element paint at opacity 0 until hydration, wrecking LCP.
   */
  instant?: boolean;
  /** Scale the image slightly on hover of the nearest `group` ancestor. */
  hoverZoom?: boolean;
  /** Extra classes for the outer frame (rounding, ring, fixed sizes …). */
  className?: string;
  /** Extra classes for the `<Image>` itself. */
  imgClassName?: string;
  /** Fallback letter size, defaults to `text-4xl`. */
  fallbackClassName?: string;
  /** Overlay content (badges, hover gradients, stat pills). */
  children?: ReactNode;
}

export function AssetThumb({
  src,
  alt,
  title,
  aspect = "4/3",
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  priority,
  instant,
  hoverZoom,
  className,
  imgClassName,
  fallbackClassName,
  children,
}: AssetThumbProps) {
  const fallbackLetter = (title ?? alt ?? "").trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={cn("relative overflow-hidden bg-muted", ASPECT_CLASS[aspect], className)}>
      {src ? (
        <>
          {/* Blurred, saturated fill so non-4:3 uploads never show flat
              letterbox bars — the real image (below) always stays fully
              visible, uncropped. */}
          <Image
            src={src}
            alt=""
            aria-hidden
            fill
            sizes="64px"
            quality={10}
            className="object-cover scale-110 blur-2xl opacity-50 saturate-150"
          />
          <Image
            src={src}
            alt={alt}
            fill
            sizes={sizes}
            priority={priority}
            className={cn(
              "object-contain transition-[opacity,transform] duration-500",
              hoverZoom && "group-hover:scale-[1.04]",
              imgClassName,
            )}
            style={instant ? undefined : { opacity: 0 }}
            onLoad={instant ? undefined : (e) => (e.currentTarget.style.opacity = "1")}
          />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted/50">
          <span
            className={cn(
              "font-display font-bold text-muted-foreground/20",
              fallbackClassName ?? "text-4xl",
            )}
          >
            {fallbackLetter}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}
